import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from "@angular/core";
import {forkJoin, Observable, Subject} from "rxjs";
import {Device, KeyVarElement, Site} from "../../../../domain/cloud-topology";
import {faPlusCircle, faTimesCircle} from "@fortawesome/free-solid-svg-icons";
import {FormArray, FormControl, FormGroup, Validators} from "@angular/forms";
import {SiteDeviceService} from "../../../../services/site/site-device.service";
import {DENSTORE} from "../../../../domain/constants";
import {debounceTime, distinctUntilChanged, filter, map} from "rxjs/operators";
import {MatSnackBar} from "@angular/material/snack-bar";

@Component({
    selector: 'app-site-storage',
    templateUrl: './site-storage.component.html',
    styleUrls: ['./site-storage.component.scss'],
    standalone: false
})
export class SiteStorageComponent implements OnInit, OnChanges, OnDestroy {

  @Input() site: Site;
  @Output() formDirty: EventEmitter<boolean> = new EventEmitter<boolean>();

  faPlusCircle = faPlusCircle;
  faTimesCircle = faTimesCircle;
  panelOpenState = false;
  private subject$: Subject<void>;
  batteries: KeyVarElement[] = [];
  devices: FormArray;
  parentForm: FormGroup;
  storageDevices;

  formatter = (battery: string) => `${battery}`;

  search = (text$: Observable<string>) => text$.pipe(
    debounceTime(200),
    distinctUntilChanged(),
    filter(term => term.length >= 2),
    map(term => this.storageDevices.filter(searchItem => new RegExp(term, 'mi').test(searchItem.name)).slice(0, 10))
  )

  constructor(private siteDeviceService: SiteDeviceService,
              private snackBar: MatSnackBar) { }


  ngOnInit(): void {
    this.subject$ = new Subject<void>();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.setData();
  }

  private setData() {
    this.devices = new FormArray([]);
    this.storageDevices = [];
    this.batteries = [];
    if (this.site) {
      forkJoin([
        this.siteDeviceService.getDevices(this.site.id),
        this.siteDeviceService.getBatterySystems()
      ]).subscribe(data => {
        const devices =  data[0];
        for (const batteryStr of data[1]) {
          this.batteries.push({
            key: batteryStr,
            value: batteryStr,
          });
        }
        if (devices) {
          for (const device of devices) {
            if(device.config.type == DENSTORE) {
              this.storageDevices.push(device);
            }
          }

          for (const storageDevice of this.storageDevices) {
            const device = new FormGroup({
              id: new FormControl(storageDevice.store.id),
              deviceName: new FormControl(storageDevice.store.name, Validators.required),
              systemType: new FormControl(storageDevice.config['batterySystem'], Validators.required),
              namePlateEnergy: new FormControl(+storageDevice.config['nameplateEnergy']),
              configuredPower: new FormControl(storageDevice.config['availablePower'], Validators.required),
              numberOfRacks: new FormControl(storageDevice.config['numRacks'], [Validators.required, Validators.min(1)]),
              minimumSoc: new FormControl(storageDevice.config['minSoc'], Validators.required),
              maxSoc: new FormControl(storageDevice.config['maxSoc'], Validators.required),
              stateOfHealth: new FormControl(storageDevice.config['stateOfHealth'], Validators.required),
              usableEnergy: new FormControl(+storageDevice.config['usableEnergy'])
            });
            this.devices.push(device);
          }
        }

        this.parentForm = new FormGroup({
          devices: this.devices
        });
      });
    }

  }

  ngOnDestroy(): void {
    this.subject$.next();
    this.subject$.complete();
  }

  setDirtyForm(): void {
    this.formDirty.emit(true);
  }

  removeDevice(device): void {
    if (window.confirm(`Are you sure you want to remove this device?`)) {
      for (const deviceIdx in this.storageDevices) {
        if ((deviceIdx == device)) {
          if (this.storageDevices[deviceIdx].store.id == undefined) {
            this.storageDevices.splice(deviceIdx,1);
            this.devices.removeAt(device);
            this.snackBar.open('Device deleted', "Close",{
              duration: 3000,
              horizontalPosition: "right",
              verticalPosition: "bottom"
            });
            break;
          } else {
            this.siteDeviceService.deleteDevice(this.storageDevices[deviceIdx].store.id).subscribe(() => {
              this.snackBar.open('Storage device deleted', "Close",{
                duration: 3000,
                horizontalPosition: "right",
                verticalPosition: "bottom"
              });
              this.devices.removeAt(device);
              this.storageDevices.splice(deviceIdx, 1);
              this.formDirty.emit(false);
            }, (err) => {
              this.snackBar.open('Problem deleting device', "Close",{
                duration: 3000,
                horizontalPosition: "right",
                verticalPosition: "bottom"
              });
              console.error(`Problem deleting device. reason: ${err}`);
            });
          }
        }
      }
    }
  }

  saveStorage(): void {
    if(this.site) {
      if(window.confirm(`Are you sure you want to save storage devices for ${this.site.name}?`)){
        this.translateDeviceForm();
        const currentDevices = [];
        const newDevices = [];
        for (const storage of this.storageDevices) {
          if(storage.store.id != undefined) {
            currentDevices.push(storage);
          } else {
            newDevices.push(storage);
          }
        }

        let subscriptions = [];
        if (currentDevices.length) {
          subscriptions.push(this.siteDeviceService.putDevices(this.site.id, currentDevices));
        }

        if (newDevices.length) {
          subscriptions.push(this.siteDeviceService.postDevices(this.site.id, newDevices));
        }

        if (subscriptions.length) {
          forkJoin(subscriptions).subscribe(() => {
            this.formDirty.emit(false);
            this.setData();
            this.snackBar.open(`Storage devices updated for ${this.site.name}.`, "Close",{
              duration: 3000,
              horizontalPosition: "right",
              verticalPosition: "bottom"
            });
          }, (error) => {
            console.log(`Problem saving storage devices reason: ${error}`);
            this.snackBar.open(`Problem saving devices for ${this.site.name}.`, "Close",{
              duration: 3000,
              horizontalPosition: "right",
              verticalPosition: "bottom"
            });
          });
        }
      }
    }
  }

  cancel(): void {
    this.parentForm.reset();
    this.formDirty.emit(false);
    this.setData();
  }

  addBlankDevice(): void {
    const blankDevice =
      new FormGroup({
        id: new FormControl(''),
        deviceName: new FormControl('New Storage Device', Validators.required),
        systemType: new FormControl('', Validators.required),
        configuredPower: new FormControl(0, Validators.required),
        namePlateEnergy: new FormControl(0, Validators.required),
        numberOfRacks: new FormControl(1, [Validators.required, Validators.min(1)]),
        minimumSoc: new FormControl(0, Validators.required),
        maxSoc: new FormControl(100, Validators.required),
        stateOfHealth: new FormControl(100, Validators.required),
        usableEnergy: new FormControl(0)
      });

    this.storageDevices.push({
      store: { name: 'New Storage Device'},
      config: {
        'available-power': 0,
        'battery-system': 0,
        'max-soc': 100,
        'min-soc': 0,
        'nameplate-energy': 0,
        'numRacks': 1,
        'state-of-health': 100
      }
    });
    this.devices.push(blankDevice);
  }

  private translateDeviceForm() {
    let storageDevices: Device[] = [];
    for (const deviceForm of this.devices.value) {
      let storageDevice = {
        store: {
          name: deviceForm.deviceName,
        },
        config: {
          type: DENSTORE,
          'battery-system': deviceForm.systemType,
          'min-soc': deviceForm.minimumSoc,
          'max-soc': deviceForm.maxSoc,
          'available-power': deviceForm.configuredPower,
          numRacks: deviceForm.numberOfRacks,
          'state-of-health': deviceForm.stateOfHealth,
        }
      };

      if(deviceForm.id != '') {
        storageDevice.store['id'] = deviceForm.id;
        storageDevice.store['siteId'] = this.site.id;
      }

      storageDevices.push(storageDevice);
    }

    this.storageDevices = storageDevices;
  }
}
